/**
 *  EvolutionComponent script file
 **/
import * as d3 from 'd3'
import { formatNumber } from '@/utils/formatNumber'
import { normalizeString } from '@/utils/normalizeString'
import { clientMixin } from '../../mixins/fetch'

export default {
  name: 'EvolutionComponent',
  mixins: [clientMixin],
  components: {},
  props: {
    properties: {
      type: Object,
      required: true
    }
  },
  data () {
    return {
      config: this.properties.config,
      colors: this.properties.config.colors,
      spinner:
        (this.properties.config &&
          this.properties.config.colors &&
          this.properties.config.colors.spinner) ||
        {},
      color: undefined,
      data: [],
      crosshair: null,
      tooltip: null,
      tooltipData: null,
      itemSelected: undefined,
      currentDate: [],
      currentData: [],
      currentYear: undefined,
      currentMonth: undefined,
      years: [],
      yearsData: {},
      clicked: false,
      uid: null,
      evolution: []
    }
  },
  created () {
    this.uid = `component-${this._uid}`
  },
  async mounted () {
    await this.getEvolution()

    this.$nextTick(function () {
      if (!this.isLoading) {
        this.init()
      }
    })

    window.addEventListener('resize', this.clearChart, false)
  },
  computed: {
    selectData () {
      return [
        ...new Set(
          ...this.evolution.map(x => Object.keys(x).filter(x => x !== 'date'))
        )
      ].map(x => ({ id: x, name: x }))
    },
    total () {
      const current = this.currentData[1] || {}
      const values = Object.entries(current).filter(x => x[0] !== 'date')
      const data = values.reduce((a, c) => {
        if (!this.itemSelected) {
          a += c[1]
        } else if (this.itemSelected.id === c[0]) {
          a += c[1]
        }
        return a
      }, 0)

      return formatNumber(data)
    },
    percentage () {
      if (!this.currentData.length) {
        return 0
      }

      const currentValueKey = Object.entries(this.currentData[1]).filter(
        x => x[0] !== 'date'
      )
      const currentValue = currentValueKey.reduce((a, c) => {
        a += c[1]
        return a
      }, 0)

      const oldValueKey = Object.entries(this.currentData[0]).filter(
        x => x[0] !== 'date'
      )
      const oldValue = oldValueKey.reduce((a, c) => {
        a += c[1]
        return a
      }, 0)

      // const result = oldValue ? (Math.abs(currentValue - oldValue) / Math.abs((currentValue + oldValue) / 2)) * 100 : 0
      const result = oldValue
        ? (Math.abs(currentValue - oldValue) / oldValue) * 100
        : 0
      return formatNumber(currentValue < oldValue ? result * -1 : result)
    }
  },
  methods: {
    async getEvolution () {
      const { data } = this.properties

      if (!Array.isArray(data) || !data.length) {
        return
      }

      try {
        this.isLoading = true

        const [evolution] = await Promise.all(
          this.properties.data.map(async file => {
            try {
              return await this.getData(file)
            } catch (error) {
              return null
            }
          })
        )

        this.evolution = this.formatData(evolution)
      } catch (error) {
      } finally {
        this.isLoading = false
      }
    },
    formatData (data = []) {
      return data.map(d => ({
        ...d,
        date: this.parseTime(d.date)
      }))
    },
    parseTime (date) {
      const parseTime = d3.timeParse('%Y-%m-%d')

      return parseTime(date)
    },
    getMonth (month) {
      return this.config.months[month.getMonth()]
    },
    init () {
      this.generateData(this.evolution, true)
    },
    setCurrentDate () {
      const date = this.evolution[this.evolution.length - 1].date
      this.currentYear = date.getFullYear()
      this.currentMonth = date.getMonth()
    },
    generateData (data, generateColor = false) {
      this.setCurrentDate()

      const columns = Array.from(
        new Set(data.flatMap(l => Object.keys(l)))
      ).filter(c => c !== 'date')
      const series = d3.stack().keys(columns)(data)

      if (generateColor) {
        this.color = d3
          .scaleOrdinal()
          .domain(series)
          .range(this.colors.charts.palette)
      }

      this.drawChart(data, series)
    },
    drawChart (data, series) {
      d3.select(`#${this.uid} .chart-wrapper .svg`)
        .selectAll('*')
        .remove()

      const wrapper = this.$refs.container.getBoundingClientRect()
      const padding = 40
      const width = wrapper.width
      const height = 220
      const xScale = d3
        .scaleTime()
        .domain(d3.extent(data, d => d.date))
        .range([0, width])
      const yScale = d3
        .scaleLinear()
        .domain([0, d3.max(series, d => d3.max(d, d => d[1]))])
        .nice()
        .range([height - padding, padding])
      const area = d3
        .area()
        .x(d => xScale(d.data.date))
        .y0(d => yScale(d[0]))
        .y1(d => yScale(d[1]))
        .curve(d3.curveMonotoneX)

      this.tooltip = d3.select(this.$refs.tooltip).style('opacity', 0)
      d3.timeFormatDefaultLocale({
        decimal: ',',
        thousands: '.',
        grouping: [3],
        currency: ['€', ''],
        dateTime: '%a %b %e %X %Y',
        date: '%d/%m/%Y',
        time: '%H:%M:%S',
        periods: ['AM', 'PM'],
        days: ['Domingo', 'Lunes', 'Martes', 'Miércoles', 'Jueves', 'Viernes', 'Sábado'],
        shortDays: ['Dom', 'Lun', 'Mar', 'Mi', 'Jue', 'Vie', 'Sab'],
        months: ['Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio', 'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre'],
        shortMonths: this.config.months
      })
      const xAxis = g =>
        g
          .attr('transform', `translate(0, ${height - padding / 2})`)
          .call(
            d3
              .axisBottom(xScale)
              // .ticks(width / 80)
              .tickFormat(d3.timeFormat(' %b'))
          )
          .selectAll(`#${this.uid} text`)
          .attr('font-family', this.config.font)
          .attr('font-size', '11px')
          .attr('fill', this.colors.text.low)
          .style('text-anchor', (d, i) => (i === 0 ? 'start' : 'end'))

      const svg = d3
        .select(this.$refs.svg)
        .attr('preserveAspectRatio', 'xMinYMin meet')
        .attr('viewBox', [0, 0, width, height])
        .attr('width', `${width}px`)
        .attr('height', `${height}px`)

      svg
        .append('g')
        .attr('transform', `translate(0 ${padding / 2})`)
        .selectAll(`#${this.uid} path`)
        .data(series)
        .join('path')
        .attr('class', d => `area path-${d.index}`)
        .attr(
          'id',
          d =>
            normalizeString(d.key)
              .toLowerCase()
              .split(' ')
              .join('-') + '-evolution'
        )
        .attr('fill', ({ key }) => this.color(key))
        .attr('d', area)
        .style('cursor', 'pointer')
        .on('click', d => this.onPathClick(d, xScale, wrapper))
        .on('mouseover', this.onPathOver)
        .on('mousemove', d => this.onPathMouseMove(d, xScale, wrapper))
        .on('mouseout', this.onPathOut)

      svg
        .append('g')
        .attr('class', 'tick')
        .call(xAxis)

      d3.selectAll(`#${this.uid} .tick line`).attr('display', 'none')
      d3.selectAll(`#${this.uid} .domain`).attr('display', 'none')

      this.crosshair = svg
        .append('line')
        .attr('class', 'crosshair')
        .attr('stroke', 'rgba(56, 61, 76, 0.4)')
        .style('opacity', 0)
        .style('pointer-events', 'none')
    },
    onPathClick (path, xScale, wrapper) {
      this.$gtm.trackEvent({
        event: 'event_graph', // Event type [default = 'interaction'] (Optional)
        category: 'evolution:' + this.config.mapActiveTag,
        action: path.key,
        label: this.currentDate.toString(),
        value: 1,
        noninteraction: false // Optional
      })
      this.clicked = true
      const data = this.evolution.reduce((acum, current) => {
        acum.push({
          date: current.date,
          [path.key]: current[path.key]
        })
        return acum
      }, [])

      this.itemSelected = {
        id: path.key,
        name: path.key
      }

      // this.onPathMouseMove(path, xScale, wrapper)
      this.generateData(data)
    },
    setSelection (node) {
      this.setAll()
      const str =
        normalizeString(node)
          .toLowerCase()
          .split(' ')
          .join('-') + '-evolution'
      const el = document.querySelector(`#${this.uid} #${str}`)
      /* const el = document.querySelector(`#${node.split(' ').join('-') + '-evolution'}`) */
      el.dispatchEvent(new Event('click'))
      this.$refs.select.close()
    },
    setAll () {
      this.clicked = false
      this.itemSelected = undefined
      this.generateData(this.evolution)
      this.$refs.select.close()
    },
    onPathOver (path) {
      d3.selectAll(`#${this.uid} .area:not(.path-${path.index})`)
        .transition()
        .duration(250)
        .ease(d3.easeLinear)
        .style('opacity', 0.2)
    },
    onPathMouseMove (path, xScale, wrapper) {
      const key = path.key
      const mouse = d3.mouse(d3.event.target)
      const date = xScale.invert(mouse[0] + 1)

      this.currentYear = date.getFullYear()
      this.currentMonth = date.getMonth()

      let indexMonth =
        this.evolution.findIndex(
          x =>
            x.date.getMonth() === this.currentMonth &&
            x.date.getFullYear() === this.currentYear
        ) || {}

      if (indexMonth < 0) {
        indexMonth = indexMonth - 1
      }

      const value = (this.evolution[indexMonth] || {})[key] || 0

      let oldData = this.evolution[indexMonth - 1]

      if (!oldData) {
        oldData = this.evolution[indexMonth - 2]
      }

      const oldValue = (oldData || {})[key] || 0
      const percentage = oldValue
        ? (Math.abs(value - oldValue) / oldValue) * 100
        : 0
      // const percentage = oldValue ? (Math.abs(value - oldValue) / Math.abs((value + oldValue) / 2)) * 100 : 0

      this.tooltipData = {
        job: key,
        color: this.color(key),
        value,
        percentage: formatNumber(
          value < oldValue ? percentage * -1 : percentage
        ),
        date: this.currentDate
      }

      this.tooltip
        .style('left', `${mouse[0] + 22}px`)
        .style(
          'transform',
          mouse[0] < wrapper.width / 2
            ? 'translate3d(0, 0, 0)'
            : 'translate3d(-100%, 0, 0)'
        )
        .transition()
        .duration(250)
        .ease(d3.easeLinear)
        .style('opacity', 1)

      this.crosshair
        .attr('x1', mouse[0])
        .attr('y1', 0)
        .attr('x2', mouse[0])
        .attr('y2', 200)
        .transition()
        .duration(250)
        .ease(d3.easeLinear)
        .style('opacity', 1)
    },
    onPathOut () {
      if (!this.clicked) {
        d3.selectAll(`#${this.uid} .area`)
          .transition()
          .duration(250)
          .ease(d3.easeLinear)
          .style('opacity', 1)
      }

      this.tooltip
        .transition()
        .duration(250)
        .ease(d3.easeLinear)
        .style('opacity', 0)

      this.crosshair
        .transition()
        .duration(250)
        .ease(d3.easeLinear)
        .style('opacity', 0)
    },
    clearChart () {
      this.init()
    }
  },
  watch: {
    currentMonth (value) {
      const indexMonth = this.evolution.findIndex(
        x =>
          x.date.getMonth() === value &&
          x.date.getFullYear() === this.currentYear
      )
      const firstValue = this.evolution[indexMonth - 1]
      const secondValue = this.evolution[indexMonth]

      this.currentData = [
        firstValue || {
          date: new Date()
        },
        secondValue || {
          date: new Date()
        }
      ]
    },
    currentData (value) {
      this.currentDate = [
        this.getMonth(value[1].date),
        value[0].date.getFullYear()
      ]
    }
  }
}
